import React, { useCallback, useMemo, useState } from "react";
import { useLanguage } from "../i18n/LanguageContext";
import { formatMoneyInput, moneyInputProps, parseMoneyInput } from "../utils/priceFormat";
import ExpandableText from "../components/ExpandableText";
import ReminderPanel, { ReminderWarning, reminderPayloadKontrolEt } from "../components/ReminderPanel";
import { listLabel } from "../utils/i18nOptions";
import ReceiptPreviewModal from "../components/ReceiptPreviewModal";
import AsyncActionButton from "../components/AsyncActionButton";
import FormValidationNotice, { useFormValidation } from "../components/FormValidationNotice";
import { fotografSec, metinTani, marketFisiParse } from "../services/mlkitService";
import { harcamalariTopluEkleService } from "../services/sharedExpenseService";

const SHARED_EXPENSE_CATEGORY_LABELS_EN = {
  "Ev": "Home",
  "ev": "Home",
  "Diğer": "Other",
  "diğer": "Other",
  "Market": "Groceries",
  "market": "Groceries",
  "Gıda": "Food",
  "gıda": "Food",
  "Yemek": "Food",
  "yemek": "Food",
  "Ulaşım": "Transportation",
  "ulaşım": "Transportation",
  "Araç": "Vehicle",
  "araç": "Vehicle",
  "Yakıt": "Fuel",
  "yakıt": "Fuel",
  "Fatura": "Bills",
  "fatura": "Bills",
  "Faturalar": "Bills",
  "faturalar": "Bills",
  "Kira": "Rent",
  "kira": "Rent",
  "Aidat": "Dues",
  "aidat": "Dues",
  "Sağlık": "Health",
  "sağlık": "Health",
  "Eğitim": "Education",
  "eğitim": "Education",
  "Çocuk": "Child",
  "çocuk": "Child",
  "Bebek": "Baby",
  "bebek": "Baby",
  "Eğlence": "Entertainment",
  "eğlence": "Entertainment",
  "Alışveriş": "Shopping",
  "alışveriş": "Shopping",
  "Temizlik": "Cleaning",
  "temizlik": "Cleaning",
  "Misafir": "Guests",
  "misafir": "Guests",
  "Kredi Kartı": "Credit Card",
  "kredi kartı": "Credit Card",
};

function kategoriOzeti(items) {
  const map = {};
  items.forEach((h) => {
    const key = h.kategori || "Diğer";
    map[key] = (map[key] || 0) + Number(h.tutar || 0);
  });
  return Object.entries(map).map(([kategori, tutar]) => ({ kategori, tutar })).sort((a, b) => b.tutar - a.tutar);
}

function harcamaTarihiniAl(harcama) {
  if (!harcama) return null;
  if (harcama.tarih) return harcama.tarih;
  if (harcama.createdAt?.seconds) return new Date(harcama.createdAt.seconds * 1000).toISOString();
  if (harcama.created_at) return harcama.created_at;
  if (harcama.createdAt) return harcama.createdAt;
  return null;
}

function harcamaDonemeUygunMu(harcama, periyot, seciliAy, seciliYil) {
  const tarih = harcamaTarihiniAl(harcama);
  if (!tarih) return false;
  const d = new Date(tarih);
  if (Number.isNaN(d.getTime())) return false;
  if (periyot === "Yıllık") return d.getFullYear() === seciliYil;
  if (seciliAy === -1) return d.getFullYear() === seciliYil;
  return d.getFullYear() === seciliYil && d.getMonth() === seciliAy;
}

function kullaniciAdiAl(u) {
  const adSoyad = `${u?.ad || ""} ${u?.soyad || ""}`.trim();
  return adSoyad || u?.displayName || u?.name || u?.email || "";
}

function MiniBarList({ items, total, paraFormatla, emptyLabel, categoryLabel = (value) => value, limit = 6 }) {
  if (!items.length) return <div className="emptyState compact">{emptyLabel}</div>;
  return (
    <div className="miniBarList">
      {items.slice(0, limit).map((item) => {
        const pct = total > 0 ? Math.min(100, (item.tutar / total) * 100) : 0;
        return (
          <div className="miniBarItem" key={item.kategori || item.isim}>
            <div className="miniBarTop">
              <span>{item.kategori ? categoryLabel(item.kategori) : item.isim}</span>
              <b>{paraFormatla(item.tutar)}</b>
            </div>
            <div className="miniBarTrack"><span style={{ width: `${pct}%` }} /></div>
          </div>
        );
      })}
    </div>
  );
}

function SharedExpensesPage({
  harcamaFormAcik, setHarcamaFormAcik,
  borcHesabi, borcHesabiDegistir,
  harcamaBaslik, setHarcamaBaslik,
  harcamaTutar, setHarcamaTutar,
  harcamaKategori, setHarcamaKategori,
  harcamaTarih, setHarcamaTarih,
  harcamaEkle, toplamOrtakHarcama,
  kisiBazli, borcOzeti, buAyHarcama, tumHarcamalar, uyeler = [],
  harcamaSil, harcamaKategorileri,
  paraFormatla, permissions, user,
  familyId, tamAd,
  onReminderKur,
}) {
  const { t, tList, lang } = useLanguage();
  const expenseValidation = useFormValidation("shared-expense-create");
  const AYLAR = tList("common.months");
  const [harcamaPeriyot, setHarcamaPeriyot] = useState("Aylık");
  const [harcamaSeciliAy, setHarcamaSeciliAy] = useState(new Date().getMonth());
  const [harcamaSeciliYil, setHarcamaSeciliYil] = useState(new Date().getFullYear());

  // ── Fiş Tarama state ──
  const [fistScanAcik,    setFisScanAcik]    = useState(false);
  const [fisScanYukleni,  setFisScanYukleni]  = useState(false);
  const [fisScanHata,     setFisScanHata]     = useState("");
  const [fisKalemleri,    setFisKalemleri]    = useState([]);
  const [fisEklendi,      setFisEklendi]      = useState(false);
  const [fisIlerleme,     setFisIlerleme]     = useState(0);

  const handleFisScan = useCallback(async (kaynak) => {
    setFisScanHata("");
    setFisEklendi(false);
    setFisIlerleme(0);
    setFisScanYukleni(true);
    try {
      const { dataUrl } = await fotografSec(kaynak);
      const rawText = await metinTani(dataUrl, (pct) => setFisIlerleme(pct));
      const parsed  = marketFisiParse(rawText);
      if (!parsed.length) {
        setFisScanHata(lang === "en"
          ? "No items detected. Try a clearer photo."
          : "Ürün tespit edilemedi. Fişin tamamı görünür olsun.");
        return;
      }
      setFisKalemleri(parsed);
      setFisScanAcik(true);
    } catch (err) {
      if (err?.message === "CANCELLED") return;
      if (err?.message === "NATIVE_OCR_REQUIRED") {
        setFisScanHata(lang === "en"
          ? "Receipt scanning works on the installed iPhone app. Please try on the device."
          : "Fiş okuma yüklü iPhone uygulamasında çalışır. Lütfen cihazda deneyin.");
      } else {
        setFisScanHata(lang === "en" ? "Scan failed. Please try again." : "Tarama başarısız. Tekrar deneyin.");
      }
    } finally {
      setFisScanYukleni(false);
      setFisIlerleme(0);
    }
  }, [lang]);

  const handleFisConfirm = useCallback(async (kalemler) => {
    if (!familyId || !user) return false;
    const bugun = new Date().toISOString().slice(0, 10);
    const result = await harcamalariTopluEkleService({
      familyId,
      kalemler,
      harcamaKategori: "Market",
      harcamaTarih: bugun,
      tamAd,
      user,
    });
    if ((result?.count || 0) > 0) {
      setFisScanAcik(false);
      setFisEklendi(true);
      setTimeout(() => setFisEklendi(false), 3000);
      return true;
    }
    return false;
  }, [familyId, user, tamAd, harcamaKategorileri]);
  const harcamaKategoriEtiketi = (kategori) => {
    const raw = String(kategori || "Diğer").trim();
    const translated = listLabel(tList, "const.expenseCategories", raw);
    if (translated && translated !== raw) return translated;
    if (lang === "en") {
      return SHARED_EXPENSE_CATEGORY_LABELS_EN[raw] || SHARED_EXPENSE_CATEGORY_LABELS_EN[raw.toLowerCase()] || raw;
    }
    return raw;
  };

  function tarihOkunakli(str) {
    if (!str) return "";
    const [y, m, d] = str.split("-");
    return `${parseInt(d)} ${AYLAR[parseInt(m) - 1]} ${y}`;
  }

  const canAdd       = permissions?.actions?.addSharedExpense;
  const canDeleteOwn = permissions?.actions?.deleteOwnSharedExpense;
  const canDeleteAll = permissions?.actions?.deleteAllSharedExpense;
  const [kisiBazliAcik, setKisiBazliAcik] = useState(false);
  const [harcamaListeGenisletildi, setHarcamaListeGenisletildi] = useState(false);
  const [reminderAktif, setReminderAktif] = useState(false);
  const [reminderTarih, setReminderTarih] = useState("");
  const [reminderSaat, setReminderSaat] = useState("09:00");
  const [reminderTekrar, setReminderTekrar] = useState("none");
  const [reminderError, setReminderError] = useState("");

  const yilSecenekleri = useMemo(() => {
    const now = new Date();
    const years = new Set();
    for (let y = now.getFullYear() - 4; y <= now.getFullYear() + 1; y += 1) years.add(y);
    (tumHarcamalar || []).forEach((h) => {
      const tarih = harcamaTarihiniAl(h);
      const d = tarih ? new Date(tarih) : null;
      if (d && !Number.isNaN(d.getTime())) years.add(d.getFullYear());
    });
    return [...years].sort((a, b) => b - a);
  }, [tumHarcamalar]);

  const filtreliHarcama = useMemo(() => {
    const kaynak = Array.isArray(tumHarcamalar) ? tumHarcamalar : (buAyHarcama || []);
    return kaynak.filter((h) => harcamaDonemeUygunMu(h, harcamaPeriyot, harcamaSeciliAy, harcamaSeciliYil));
  }, [tumHarcamalar, buAyHarcama, harcamaPeriyot, harcamaSeciliAy, harcamaSeciliYil]);

  const toplamFiltreliHarcama = useMemo(() => filtreliHarcama.reduce((a, b) => a + Number(b.tutar || 0), 0), [filtreliHarcama]);

  const kisiBazliFiltreli = useMemo(() => {
    const aileUyeleri = Array.isArray(uyeler) ? uyeler : [];
    if (aileUyeleri.length > 0) {
      return aileUyeleri.map((u) => {
        const isim = kullaniciAdiAl(u);
        return {
          isim,
          tutar: filtreliHarcama
            .filter((h) => h.ekleyen === isim || h.ekleyenUid === u.uid || h.userId === u.uid)
            .reduce((a, b) => a + Number(b.tutar || 0), 0),
        };
      });
    }
    const map = {};
    filtreliHarcama.forEach((h) => {
      const isim = h.ekleyen || h.ekleyenAd || h.userName || (lang === "en" ? "Unknown" : "Bilinmeyen");
      map[isim] = (map[isim] || 0) + Number(h.tutar || 0);
    });
    return Object.entries(map).map(([isim, tutar]) => ({ isim, tutar }));
  }, [uyeler, filtreliHarcama, lang]);

  const borcOzetiFiltreli = useMemo(() => {
    if (!borcHesabi || kisiBazliFiltreli.length === 0) return [];
    const kisiBasiDeger = toplamFiltreliHarcama / kisiBazliFiltreli.length;
    return kisiBazliFiltreli.map((k) => ({ ...k, fark: k.tutar - kisiBasiDeger }));
  }, [borcHesabi, kisiBazliFiltreli, toplamFiltreliHarcama]);

  const siraliHarcama = useMemo(() => [...filtreliHarcama].sort((a, b) => new Date(harcamaTarihiniAl(b) || 0) - new Date(harcamaTarihiniAl(a) || 0)), [filtreliHarcama]);
  const gorunenHarcama = siraliHarcama;
  const kategoriOzetleri = useMemo(() => kategoriOzeti(filtreliHarcama), [filtreliHarcama]);
  const enYogunKategori = kategoriOzetleri[0];
  const kisiBasi = kisiBazliFiltreli.length > 0 ? toplamFiltreliHarcama / kisiBazliFiltreli.length : 0;
  const kisiBazliGorunen = kisiBazliFiltreli.filter((k) => Number(k.tutar || 0) > 0);
  const enCokOdeyen = [...kisiBazliGorunen].sort((a, b) => b.tutar - a.tutar)[0] || null;
  const donemKayitMetni = harcamaPeriyot === "Yıllık"
    ? `${harcamaSeciliYil}`
    : (harcamaSeciliAy === -1 ? `${harcamaSeciliYil}` : `${AYLAR[harcamaSeciliAy]} ${harcamaSeciliYil}`);
  const topCategoryTitle = lang === "en" ? "Top 3 Spending Categories" : "En Yüksek 3 Harcama Kategorisi";
  const totalExpenseLabel = lang === "en" ? "Shared spending overview" : "Ortak harcama özeti";

  const handleHarcamaEkle = async () => {
    if (!expenseValidation.validate([
      !String(harcamaBaslik || "").trim() && "title",
      !(parseMoneyInput(harcamaTutar) > 0) && "amount",
    ])) return;
    const baslikEtkili = String(harcamaBaslik || "").trim();
    let reminderPayload = null;
    if (reminderAktif && onReminderKur) {
      reminderPayload = reminderPayloadKontrolEt({
        mod: "datetime",
        tarih: reminderTarih,
        saat: reminderSaat,
        tekrar: reminderTekrar,
      }, t, lang, setReminderError);
      if (!reminderPayload) return;
    }

    const yeniHarcamaId = await harcamaEkle();
    if (!yeniHarcamaId) return false;

    if (yeniHarcamaId && reminderPayload && onReminderKur) {
      await onReminderKur({
          baslik: t("expense.reminderTitle"),
          aciklama: baslikEtkili
            ? t("notify.expenseReminder.item", { item: baslikEtkili })
            : t("notify.expenseReminder.default"),
          baslikKey: "expense.reminderTitle",
          aciklamaKey: baslikEtkili ? "notify.expenseReminder.item" : "notify.expenseReminder.default",
          aciklamaVars: baslikEtkili ? { item: baslikEtkili } : null,
          kaynak: "sharedExpense",
          kaynakId: yeniHarcamaId,
          kaynakTuru: "sharedExpense",
          url: "/?pushPage=harcama",
          ...reminderPayload,
        }).catch(() => {});

      setReminderAktif(false);
      setReminderTarih("");
      setReminderSaat("09:00");
      setReminderTekrar("none");
      setReminderError("");
    }
    return true;
  };

  return (
    <div className="pageContent sharedProPage">
      <style>{`
        .sharedProPage .financeListRevealWrap {
          display: flex;
          justify-content: center;
          padding: 10px 10px 12px;
        }

        .sharedProPage .financeCompactFilterCard {
          padding: 7px 8px;
          margin-bottom: 10px;
          border-radius: 16px;
          display: flex;
          align-items: center;
          gap: 8px;
          overflow-x: auto;
          overflow-y: hidden;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
        }

        .sharedProPage .financeCompactFilterCard::-webkit-scrollbar {
          display: none;
        }

        .sharedProPage .financeCompactFilterHead {
          display: inline-flex;
          align-items: center;
          flex: 0 0 auto;
          margin: 0;
        }

        .sharedProPage .financeCompactFilterHead .cardHeaderTitle {
          font-size: 12px;
          line-height: 1;
          white-space: nowrap;
          opacity: 0.82;
        }

        .sharedProPage .financeCompactFilterBody {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          flex: 0 0 auto;
          min-width: max-content;
        }

        .sharedProPage .financePeriodPills {
          display: inline-flex;
          align-items: center;
          gap: 3px;
          padding: 2px;
          border-radius: 999px;
          background: rgba(148, 163, 184, 0.14);
          box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.14);
          flex: 0 0 auto;
        }

        .sharedProPage .financePeriodPills .periodBtn {
          min-height: 28px;
          padding: 0 9px;
          border-radius: 999px;
          font-size: 11px;
          line-height: 1;
          white-space: nowrap;
        }

        .sharedProPage .compactFilterInputGroup {
          min-width: 0;
          flex: 0 0 auto;
        }

        .sharedProPage .compactFilterInputGroup .darkLabel {
          display: none;
        }

        .sharedProPage .compactFilterInputGroup .darkInput {
          min-height: 30px;
          height: 30px;
          padding: 4px 26px 4px 9px;
          font-size: 12px;
          border-radius: 999px;
          max-width: 116px;
        }

        .sharedProPage .sharedHeroCard {
          position: relative;
          overflow: hidden;
          border-radius: 24px;
          padding: 16px;
          margin: 0 0 12px;
          background:
            radial-gradient(circle at 16% 12%, rgba(167, 139, 250, 0.34), transparent 34%),
            linear-gradient(135deg, rgba(124, 58, 237, 0.16), rgba(59, 130, 246, 0.08)),
            var(--card-bg, rgba(255,255,255,0.92));
          border: 1px solid rgba(124, 58, 237, 0.18);
          box-shadow: 0 16px 38px rgba(31, 41, 55, 0.10);
        }

        .dark .sharedProPage .sharedHeroCard,
        .sharedProPage.dark .sharedHeroCard {
          background:
            radial-gradient(circle at 16% 12%, rgba(167, 139, 250, 0.28), transparent 36%),
            linear-gradient(135deg, rgba(88, 28, 135, 0.58), rgba(15, 23, 42, 0.92)),
            rgba(15, 23, 42, 0.96);
          border-color: rgba(196, 181, 253, 0.18);
          box-shadow: 0 18px 42px rgba(0, 0, 0, 0.30);
        }

        .sharedProPage .sharedHeroTop {
          position: relative;
          z-index: 1;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 12px;
        }

        .sharedProPage .sharedHeroText {
          min-width: 0;
          flex: 1;
        }

        .sharedProPage .financeEyebrow {
          display: inline-flex;
          align-items: center;
          width: fit-content;
          padding: 5px 9px;
          border-radius: 999px;
          font-size: 11.5px;
          font-weight: 800;
          letter-spacing: 0.01em;
          color: #6d28d9;
          background: rgba(124, 58, 237, 0.11);
        }

        .dark .sharedProPage .financeEyebrow,
        .sharedProPage.dark .financeEyebrow {
          color: #ede9fe;
          background: rgba(196, 181, 253, 0.16);
        }

        .sharedProPage .sharedHeroCard h2 {
          margin: 9px 0 2px;
          font-size: clamp(25px, 7vw, 34px);
          line-height: 1.02;
          font-weight: 900;
          color: var(--text-primary, #111827);
          letter-spacing: -0.04em;
        }

        .dark .sharedProPage .sharedHeroCard h2,
        .sharedProPage.dark .sharedHeroCard h2 {
          color: #ffffff;
        }

        .sharedProPage .sharedHeroCard p {
          margin: 0;
          font-size: 12.8px;
          font-weight: 700;
          color: var(--text-secondary, #6b7280);
        }

        .dark .sharedProPage .sharedHeroCard p,
        .sharedProPage.dark .sharedHeroCard p {
          color: rgba(255,255,255,0.72);
        }

        .sharedProPage .sharedHeroBadge {
          flex: 0 0 auto;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 42px;
          height: 42px;
          border-radius: 16px;
          font-size: 21px;
          background: rgba(124, 58, 237, 0.12);
          box-shadow: inset 0 0 0 1px rgba(124, 58, 237, 0.13);
        }

        .dark .sharedProPage .sharedHeroBadge,
        .sharedProPage.dark .sharedHeroBadge {
          background: rgba(255,255,255,0.10);
          box-shadow: inset 0 0 0 1px rgba(255,255,255,0.12);
        }

        .sharedProPage .sharedHeroMetrics {
          position: relative;
          z-index: 1;
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 8px;
        }

        .sharedProPage .sharedMetricPill {
          min-width: 0;
          border-radius: 16px;
          padding: 10px 8px;
          background: rgba(255, 255, 255, 0.64);
          border: 1px solid rgba(124, 58, 237, 0.12);
          box-shadow: 0 8px 22px rgba(31, 41, 55, 0.06);
        }

        .dark .sharedProPage .sharedMetricPill,
        .sharedProPage.dark .sharedMetricPill {
          background: rgba(255, 255, 255, 0.09);
          border-color: rgba(255, 255, 255, 0.12);
          box-shadow: none;
        }

        .sharedProPage .sharedMetricPill small {
          display: block;
          margin-bottom: 5px;
          font-size: 10.5px;
          line-height: 1.15;
          font-weight: 800;
          color: var(--text-secondary, #6b7280);
          white-space: normal;
        }

        .dark .sharedProPage .sharedMetricPill small,
        .sharedProPage.dark .sharedMetricPill small {
          color: rgba(255,255,255,0.66);
        }

        .sharedProPage .sharedMetricPill strong {
          display: block;
          min-width: 0;
          font-size: clamp(12px, 3.25vw, 14px);
          line-height: 1.2;
          font-weight: 900;
          color: var(--text-primary, #111827);
          overflow-wrap: anywhere;
          word-break: break-word;
        }

        .dark .sharedProPage .sharedMetricPill strong,
        .sharedProPage.dark .sharedMetricPill strong {
          color: #ffffff;
        }

        .sharedProPage .sharedMetricPill span {
          display: block;
          margin-top: 4px;
          font-size: 10.5px;
          line-height: 1.15;
          font-weight: 700;
          color: var(--text-secondary, #6b7280);
          white-space: normal;
          overflow-wrap: anywhere;
        }

        .dark .sharedProPage .sharedMetricPill span,
        .sharedProPage.dark .sharedMetricPill span {
          color: rgba(255,255,255,0.62);
        }

        @media (max-width: 390px) {
          .sharedProPage .sharedHeroCard { padding: 14px 12px; border-radius: 22px; }
          .sharedProPage .sharedHeroMetrics { gap: 6px; }
          .sharedProPage .sharedMetricPill { padding: 9px 6px; border-radius: 14px; }
          .sharedProPage .sharedMetricPill small,
          .sharedProPage .sharedMetricPill span { font-size: 9.8px; }
        }

        @media (max-width: 340px) {
          .sharedProPage .sharedHeroMetrics { grid-template-columns: 1fr; }
          .sharedProPage .sharedMetricPill { display: grid; grid-template-columns: 1fr auto; column-gap: 8px; align-items: center; }
          .sharedProPage .sharedMetricPill span { grid-column: 1 / -1; }
        }

        .sharedProPage .financeListRevealBtn {
          border: 0;
          min-height: 34px;
          padding: 0 14px;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          font-size: 12px;
          font-weight: 800;
          letter-spacing: 0.01em;
          color: inherit;
          cursor: pointer;
          background: linear-gradient(135deg, rgba(34, 197, 94, 0.18), rgba(16, 185, 129, 0.12));
          box-shadow: inset 0 0 0 1px rgba(34, 197, 94, 0.24), 0 10px 24px rgba(15, 23, 42, 0.10);
          -webkit-tap-highlight-color: transparent;
        }

        .sharedProPage .financeListRevealBtn:active {
          transform: scale(0.98);
        }

        .dark .sharedProPage .financeListRevealBtn,
        .sharedProPage.dark .financeListRevealBtn {
          background: linear-gradient(135deg, rgba(34, 197, 94, 0.22), rgba(16, 185, 129, 0.14));
          box-shadow: inset 0 0 0 1px rgba(74, 222, 128, 0.24), 0 12px 26px rgba(0, 0, 0, 0.22);
        }

        .sharedProPage {
          --shared-card-radius: 18px;
        }

        .sharedProPage .sharedHeroCard {
          position: relative;
          overflow: hidden;
          border-radius: 20px;
          isolation: isolate;
          transform: translateZ(0);
          clip-path: inset(0 round 20px);
          -webkit-mask-image: -webkit-radial-gradient(white, black);
          padding: 16px 16px 14px;
          margin-bottom: 10px;
          background:
            radial-gradient(circle at 14% 12%, rgba(124, 58, 237, 0.22), transparent 34%),
            radial-gradient(circle at 92% 18%, rgba(16, 185, 129, 0.18), transparent 30%),
            linear-gradient(135deg, rgba(255,255,255,0.82), rgba(248,250,252,0.66));
          box-shadow: 0 16px 38px rgba(15, 23, 42, 0.10), inset 0 0 0 1px rgba(148, 163, 184, 0.18);
        }

        .dark .sharedProPage .sharedHeroCard,
        .sharedProPage.dark .sharedHeroCard {
          background:
            radial-gradient(circle at 14% 12%, rgba(139, 92, 246, 0.30), transparent 34%),
            radial-gradient(circle at 92% 18%, rgba(45, 212, 191, 0.16), transparent 30%),
            linear-gradient(135deg, rgba(17, 24, 39, 0.96), rgba(30, 41, 59, 0.88));
          box-shadow: 0 18px 42px rgba(0, 0, 0, 0.28), inset 0 0 0 1px rgba(255,255,255,0.08);
        }

        .sharedProPage .sharedHeroCard h2 {
          font-size: 24px;
          line-height: 1.05;
          margin: 5px 0 4px;
          letter-spacing: -0.03em;
        }

        .sharedProPage .sharedHeroCard p,
        .sharedProPage .sharedHeroMini span {
          font-size: 12px;
          line-height: 1.25;
        }

        .sharedProPage .sharedHeroMini {
          border-radius: 14px;
          padding: 9px 10px;
          background: rgba(255, 255, 255, 0.44);
          box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.16);
        }

        .dark .sharedProPage .sharedHeroMini,
        .sharedProPage.dark .sharedHeroMini {
          background: rgba(15, 23, 42, 0.42);
          box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.08);
        }

        .sharedProPage .sharedStatsGrid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 8px;
          margin: 8px 0 10px;
        }

        .sharedProPage .financeInsightCard {
          min-width: 0;
          padding: 10px 8px;
          border-radius: 16px;
          gap: 6px;
        }

        .sharedProPage .financeInsightIcon {
          width: 28px;
          height: 28px;
          min-width: 28px;
          font-size: 14px;
        }

        .sharedProPage .financeInsightCard small {
          font-size: 10.5px;
          line-height: 1.1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .sharedProPage .financeInsightCard strong {
          font-size: 13px;
          line-height: 1.15;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .sharedProPage .financeInsightCard span:not(.financeInsightIcon) {
          font-size: 10.5px;
          line-height: 1.15;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .sharedProPage .darkCard,
        .sharedProPage .tableCard {
          border-radius: var(--shared-card-radius);
          margin-bottom: 10px;
        }

        .sharedProPage .cardHeader,
        .sharedProPage .tableCardHeader {
          min-height: 38px;
          padding: 10px 12px;
        }

        .sharedProPage .cardHeaderTitle,
        .sharedProPage .tableCardHeader,
        .sharedProPage .collapsibleHeader span {
          font-size: 13.5px;
          font-weight: 850;
        }

        .sharedProPage .miniBarList {
          padding: 0 12px 12px;
          gap: 8px;
        }

        .sharedProPage .miniBarTop span,
        .sharedProPage .miniBarTop b {
          font-size: 12px;
        }

        .sharedProPage .miniBarTrack {
          height: 7px;
          border-radius: 999px;
        }

        .sharedProPage .sharedExpenseScrollList {
          max-height: 320px;
          overflow-y: auto;
          overscroll-behavior: contain;
          -webkit-overflow-scrolling: touch;
          transition: max-height 0.28s cubic-bezier(0.4,0,0.2,1);
        }
        .sharedProPage .sharedExpenseScrollList.genisletildi {
          max-height: 640px;
        }
        .sharedProPage .harcamaListeHeader {
          display: flex;
          align-items: center;
          justify-content: space-between;
          cursor: pointer;
          user-select: none;
          -webkit-tap-highlight-color: transparent;
          min-height: 38px;
          padding: 10px 12px;
          font-size: 13.5px;
          font-weight: 850;
          color: var(--text-primary);
          border-radius: var(--shared-card-radius) var(--shared-card-radius) 0 0;
          transition: background 0.15s;
        }
        .sharedProPage .harcamaListeHeader:active {
          background: rgba(139, 92, 246, 0.08);
        }
        .sharedProPage .harcamaListeToggleBtn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 26px;
          height: 26px;
          border-radius: 8px;
          border: none;
          cursor: pointer;
          flex-shrink: 0;
          background: linear-gradient(135deg, rgba(139,92,246,0.18), rgba(99,102,241,0.12));
          color: #a78bfa;
          font-size: 14px;
          transition: transform 0.25s cubic-bezier(0.4,0,0.2,1), background 0.15s;
        }
        .sharedProPage .harcamaListeToggleBtn.acik {
          transform: rotate(180deg);
          background: linear-gradient(135deg, rgba(139,92,246,0.28), rgba(99,102,241,0.20));
        }
        body[data-tema="light"] .sharedProPage .harcamaListeToggleBtn {
          background: linear-gradient(135deg, rgba(139,92,246,0.14), rgba(99,102,241,0.10));
          color: #7c3aed;
        }
        body[data-tema="light"] .sharedProPage .harcamaListeToggleBtn.acik {
          background: linear-gradient(135deg, rgba(139,92,246,0.22), rgba(99,102,241,0.16));
        }

        .sharedProPage .sharedExpenseScrollList .tableRow {
          min-height: 64px;
        }

        .sharedProPage .tableRowTitle {
          font-size: 13px;
        }

        .sharedProPage .tableRowSub {
          font-size: 11.5px;
        }

        .sharedProPage .financeAmount {
          font-size: 12.5px;
        }

        .sharedProPage .shoppingAddCard .formBody,
        .sharedProPage .shoppingCompactForm {
          gap: 9px;
          padding: 0 12px 12px;
        }

        .sharedProPage .darkInput {
          min-height: 38px;
          font-size: 13px;
        }

        .sharedProPage .darkLabel {
          font-size: 11px;
        }

        .sharedProPage .primaryButton {
          min-height: 38px;
          font-size: 13px;
        }

        .sharedProPage .debtToggleInline {
          margin: 8px 10px 10px;
          padding: 9px 10px;
          border-radius: 14px;
          background: rgba(124, 58, 237, 0.08);
          box-shadow: inset 0 0 0 1px rgba(124, 58, 237, 0.13);
        }

        .dark .sharedProPage .debtToggleInline,
        .sharedProPage.dark .debtToggleInline {
          background: rgba(139, 92, 246, 0.13);
          box-shadow: inset 0 0 0 1px rgba(167, 139, 250, 0.16);
        }



        .sharedProPage .sharedExpenseAddCard {
          position: relative;
          margin: 12px 0;
          border-radius: 20px;
          overflow: hidden;
          background: var(--card-bg, rgba(255,255,255,0.90));
          border: 1px solid var(--border-color, rgba(148, 163, 184, 0.20));
          box-shadow: 0 10px 24px rgba(15, 23, 42, 0.07);
        }

        .sharedProPage .sharedExpenseAddHeader {
          width: 100%;
          border: 0;
          background: transparent;
          display: grid;
          grid-template-columns: 38px 1fr 26px;
          align-items: center;
          gap: 10px;
          min-height: 58px;
          padding: 10px 12px;
          color: var(--text-main, #111827);
          text-align: left;
          cursor: pointer;
        }

        .sharedProPage .sharedExpenseAddIcon {
          width: 38px;
          height: 38px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          background: #1e3a8a;
          color: #fff;
          font-size: 24px;
          font-weight: 700;
          line-height: 1;
          box-shadow: 0 10px 18px rgba(30, 58, 138, 0.24);
        }

        .sharedProPage .sharedExpenseAddCopy {
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 0;
        }

        .sharedProPage .sharedExpenseAddCopy strong {
          color: var(--text-main, #0f172a);
          font-size: 17px;
          font-weight: 800;
          letter-spacing: -0.02em;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .sharedProPage .sharedExpenseAddCopy small {
          display: none;
        }

        .sharedProPage .sharedExpenseAddArrow {
          color: #3b82f6;
          font-size: 22px;
          font-weight: 900;
          text-align: center;
        }

        .sharedProPage .sharedExpenseAddCard.open {
          padding-bottom: 12px;
          background: var(--card-bg, rgba(255,255,255,0.90));
        }

        .sharedProPage .sharedExpenseAddBody {
          margin: 0 12px;
          padding: 0 0 2px;
        }

        .sharedProPage .sharedExpensePremiumGrid {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
          gap: 9px;
        }

        .sharedProPage .sharedPremiumField {
          position: relative;
          display: flex;
          flex-direction: column;
          justify-content: center;
          min-height: 58px;
          border-radius: 16px;
          padding: 9px 38px 9px 12px;
          background: rgba(255,255,255,0.74);
          border: 1px solid rgba(148, 163, 184, 0.22);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.56);
        }

        .sharedProPage .sharedPremiumField span {
          color: var(--text-muted, #64748b);
          font-size: 11.5px;
          font-weight: 700;
          line-height: 1.2;
          margin-bottom: 4px;
        }

        .sharedProPage .sharedPremiumField input,
        .sharedProPage .sharedPremiumField select {
          width: 100%;
          border: 0;
          outline: none;
          padding: 0;
          background: transparent;
          color: var(--text-main, #0f172a);
          font-size: 14px;
          font-weight: 750;
          min-height: 20px;
          appearance: none;
        }

        .sharedProPage .sharedPremiumField input::placeholder {
          color: rgba(100, 116, 139, 0.78);
          font-weight: 650;
        }

        .sharedProPage .sharedPremiumField b {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: rgba(99, 102, 241, 0.78);
          font-size: 17px;
          pointer-events: none;
        }

        .sharedProPage .sharedExpenseSaveButton {
          width: 100%;
          min-height: 44px;
          margin-top: 10px;
          border: 0;
          border-radius: 16px;
          background: linear-gradient(135deg, #38bdf8, #3b82f6 45%, #7c3aed 100%);
          color: #fff;
          font-size: 15px;
          font-weight: 850;
          letter-spacing: -0.01em;
          box-shadow: 0 14px 24px rgba(59, 130, 246, 0.24);
        }

        body.dark .sharedProPage .sharedExpenseAddCard,
        [data-theme="dark"] .sharedProPage .sharedExpenseAddCard,
        body[data-theme="dark"] .sharedProPage .sharedExpenseAddCard,
        body[data-tema="dark"] .sharedProPage .sharedExpenseAddCard {
          background: rgba(15, 23, 42, 0.72) !important;
          border-color: rgba(148, 163, 184, 0.18) !important;
          box-shadow: 0 14px 28px rgba(0,0,0,0.26), inset 0 0 0 1px rgba(255,255,255,0.04) !important;
        }

        body.dark .sharedProPage .sharedExpenseAddCopy strong,
        [data-theme="dark"] .sharedProPage .sharedExpenseAddCopy strong,
        body[data-theme="dark"] .sharedProPage .sharedExpenseAddCopy strong,
        body[data-tema="dark"] .sharedProPage .sharedExpenseAddCopy strong {
          color: #f8fafc !important;
        }

        body.dark .sharedProPage .sharedExpenseAddCopy small,
        [data-theme="dark"] .sharedProPage .sharedExpenseAddCopy small,
        body[data-theme="dark"] .sharedProPage .sharedExpenseAddCopy small,
        body[data-tema="dark"] .sharedProPage .sharedExpenseAddCopy small {
          color: rgba(226,232,240,0.74) !important;
        }

        body.dark .sharedProPage .sharedPremiumField,
        [data-theme="dark"] .sharedProPage .sharedPremiumField,
        body[data-theme="dark"] .sharedProPage .sharedPremiumField,
        body[data-tema="dark"] .sharedProPage .sharedPremiumField {
          background: rgba(15, 23, 42, 0.58) !important;
          border-color: rgba(148, 163, 184, 0.18) !important;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.05) !important;
        }

        body.dark .sharedProPage .sharedPremiumField input,
        [data-theme="dark"] .sharedProPage .sharedPremiumField input,
        body[data-theme="dark"] .sharedProPage .sharedPremiumField input,
        body[data-tema="dark"] .sharedProPage .sharedPremiumField input,
        body.dark .sharedProPage .sharedPremiumField select,
        [data-theme="dark"] .sharedProPage .sharedPremiumField select,
        body[data-theme="dark"] .sharedProPage .sharedPremiumField select,
        body[data-tema="dark"] .sharedProPage .sharedPremiumField select {
          color: #f8fafc !important;
        }

        body.dark .sharedProPage .sharedPremiumField span,
        [data-theme="dark"] .sharedProPage .sharedPremiumField span,
        body[data-theme="dark"] .sharedProPage .sharedPremiumField span,
        body[data-tema="dark"] .sharedProPage .sharedPremiumField span {
          color: rgba(226,232,240,0.74) !important;
        }

        @media (max-width: 390px) {
          .sharedProPage .sharedExpensePremiumGrid { gap: 8px; }
          .sharedProPage .sharedExpenseAddHeader { grid-template-columns: 36px 1fr 24px; min-height: 56px; padding: 9px 11px; }
          .sharedProPage .sharedExpenseAddIcon { width: 36px; height: 36px; font-size: 23px; }
          .sharedProPage .sharedExpenseAddCopy strong { font-size: 16px; }
          .sharedProPage .sharedExpenseAddCopy small { font-size: 11.5px; }
          .sharedProPage .sharedPremiumField { min-height: 54px; padding: 8px 32px 8px 10px; }
          .sharedProPage .sharedPremiumField input,
          .sharedProPage .sharedPremiumField select { font-size: 13px; }
        }

        @media (max-width: 390px) {
          .sharedProPage .sharedStatsGrid { gap: 6px; }
          .sharedProPage .financeInsightCard { padding: 9px 6px; }
          .sharedProPage .financeInsightIcon { display: none; }
          .sharedProPage .sharedExpenseScrollList { max-height: 290px; }
          .sharedProPage .sharedExpenseScrollList.genisletildi { max-height: 580px; }
        }


        /* Shared expenses dashboard dark-mode hardening: support body.dark, [data-theme], and [data-tema] */
        body.dark .sharedProPage .sharedHeroCard,
        [data-theme="dark"] .sharedProPage .sharedHeroCard,
        body[data-theme="dark"] .sharedProPage .sharedHeroCard,
        body[data-tema="dark"] .sharedProPage .sharedHeroCard {
          background:
            radial-gradient(circle at 14% 12%, rgba(139, 92, 246, 0.34), transparent 35%),
            radial-gradient(circle at 92% 18%, rgba(45, 212, 191, 0.18), transparent 31%),
            linear-gradient(135deg, rgba(18, 12, 32, 0.98), rgba(15, 23, 42, 0.96) 54%, rgba(28, 18, 48, 0.94)) !important;
          border-color: rgba(196, 181, 253, 0.22) !important;
          box-shadow: 0 18px 44px rgba(0, 0, 0, 0.34), inset 0 0 0 1px rgba(255,255,255,0.08) !important;
        }

        body.dark .sharedProPage .financeEyebrow,
        [data-theme="dark"] .sharedProPage .financeEyebrow,
        body[data-theme="dark"] .sharedProPage .financeEyebrow,
        body[data-tema="dark"] .sharedProPage .financeEyebrow {
          color: #ede9fe !important;
          background: rgba(196, 181, 253, 0.16) !important;
          box-shadow: inset 0 0 0 1px rgba(196, 181, 253, 0.10) !important;
        }

        body.dark .sharedProPage .sharedHeroCard h2,
        [data-theme="dark"] .sharedProPage .sharedHeroCard h2,
        body[data-theme="dark"] .sharedProPage .sharedHeroCard h2,
        body[data-tema="dark"] .sharedProPage .sharedHeroCard h2 {
          color: #ffffff !important;
          text-shadow: 0 2px 16px rgba(0,0,0,0.18);
        }

        body.dark .sharedProPage .sharedHeroCard p,
        [data-theme="dark"] .sharedProPage .sharedHeroCard p,
        body[data-theme="dark"] .sharedProPage .sharedHeroCard p,
        body[data-tema="dark"] .sharedProPage .sharedHeroCard p {
          color: rgba(241,245,249,0.76) !important;
        }

        body.dark .sharedProPage .sharedHeroBadge,
        [data-theme="dark"] .sharedProPage .sharedHeroBadge,
        body[data-theme="dark"] .sharedProPage .sharedHeroBadge,
        body[data-tema="dark"] .sharedProPage .sharedHeroBadge {
          background: rgba(255,255,255,0.10) !important;
          box-shadow: inset 0 0 0 1px rgba(255,255,255,0.12) !important;
        }

        body.dark .sharedProPage .sharedMetricPill,
        [data-theme="dark"] .sharedProPage .sharedMetricPill,
        body[data-theme="dark"] .sharedProPage .sharedMetricPill,
        body[data-tema="dark"] .sharedProPage .sharedMetricPill {
          background: rgba(15, 23, 42, 0.58) !important;
          border-color: rgba(255, 255, 255, 0.12) !important;
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.06) !important;
          backdrop-filter: blur(10px);
        }

        body.dark .sharedProPage .sharedMetricPill small,
        [data-theme="dark"] .sharedProPage .sharedMetricPill small,
        body[data-theme="dark"] .sharedProPage .sharedMetricPill small,
        body[data-tema="dark"] .sharedProPage .sharedMetricPill small,
        body.dark .sharedProPage .sharedMetricPill span,
        [data-theme="dark"] .sharedProPage .sharedMetricPill span,
        body[data-theme="dark"] .sharedProPage .sharedMetricPill span,
        body[data-tema="dark"] .sharedProPage .sharedMetricPill span {
          color: rgba(226,232,240,0.72) !important;
        }

        body.dark .sharedProPage .sharedMetricPill strong,
        [data-theme="dark"] .sharedProPage .sharedMetricPill strong,
        body[data-theme="dark"] .sharedProPage .sharedMetricPill strong,
        body[data-tema="dark"] .sharedProPage .sharedMetricPill strong {
          color: #ffffff !important;
        }

        /* Responsive expense dashboard: no horizontal dead-end on compact phones. */
        .sharedProPage .financeCompactFilterCard.sharedPeriodFilterCard {
          overflow: visible;
          flex-wrap: wrap;
          align-items: center;
        }
        .sharedProPage .sharedPeriodFilterCard .financeCompactFilterBody {
          flex: 1 1 250px;
          min-width: 0;
          flex-wrap: wrap;
        }
        .sharedProPage .sharedPeriodFilterCard .financePeriodPills { flex: 0 0 auto; }
        .sharedProPage .sharedPeriodFilterCard .compactFilterInputGroup { flex: 1 1 84px; }
        .sharedProPage .sharedPeriodFilterCard .compactFilterInputGroup .darkInput {
          width: 100%;
          max-width: none;
        }
        body.dark .sharedProPage :is(.tableRowSub, .emptyState, .darkLabel),
        [data-theme="dark"] .sharedProPage :is(.tableRowSub, .emptyState, .darkLabel),
        body[data-tema="dark"] .sharedProPage :is(.tableRowSub, .emptyState, .darkLabel) {
          color: rgba(226, 232, 240, .76) !important;
          opacity: 1;
        }
        body.dark .sharedProPage .sharedPremiumField input::placeholder,
        [data-theme="dark"] .sharedProPage .sharedPremiumField input::placeholder,
        body[data-tema="dark"] .sharedProPage .sharedPremiumField input::placeholder {
          color: rgba(203, 213, 225, .68) !important;
        }
        .sharedProPage .emptyState { min-height: 0; padding: 18px 14px; }
        .sharedProPage {
          min-width: 0;
          overflow-x: clip;
          scroll-padding-top: calc(var(--sa-top, env(safe-area-inset-top, 0px)) + 84px);
        }
        .sharedProPage::before {
          content: "";
          position: fixed;
          z-index: 90;
          top: 0;
          left: 0;
          right: 0;
          height: var(--sa-top, env(safe-area-inset-top, 0px));
          background: var(--bg-base, #eef2ff);
          pointer-events: none;
        }
        .sharedProPage :is(.sharedHeroCard, .sharedPeriodFilterCard, .sharedExpenseAddCard, .tableCard) {
          scroll-margin-top: calc(var(--sa-top, env(safe-area-inset-top, 0px)) + 84px);
        }
        .sharedProPage .periodBtn.active { color: #fff !important; }
        .sharedProPage .sharedCategorySummaryCard {
          min-height: 126px;
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
        }
        .sharedProPage .sharedCategorySummaryCard .emptyState {
          flex: 1;
          display: grid;
          place-items: center;
        }
        .sharedProPage .financeAmount.neutral { color: var(--text-main, #0f172a) !important; }
        .sharedProPage .miniToggle {
          color: #6366f1;
          background: rgba(99, 102, 241, .10);
          border: 1px solid rgba(99, 102, 241, .18);
        }
        .sharedProPage .debtToggleInline .toggleLabelText {
          color: var(--text-main, #0f172a);
          font-weight: 850;
        }
        body[data-tema="light"] .sharedProPage .rowDeleteBtn,
        [data-theme="light"] .sharedProPage .rowDeleteBtn {
          color: #b42318;
          background: #fff1f0;
          border: 1px solid rgba(180, 35, 24, .22);
        }
        body.dark .sharedProPage :is(.reminderPanel, .debtToggleInline),
        [data-theme="dark"] .sharedProPage :is(.reminderPanel, .debtToggleInline),
        body[data-tema="dark"] .sharedProPage :is(.reminderPanel, .debtToggleInline) {
          color: rgba(241, 245, 249, .92);
          background: rgba(30, 41, 59, .76) !important;
          border-color: rgba(148, 163, 184, .24) !important;
          box-shadow: inset 0 0 0 1px rgba(255, 255, 255, .04) !important;
        }
        body.dark .sharedProPage :is(.reminderCheckText, .debtToggleInline .toggleLabelText),
        [data-theme="dark"] .sharedProPage :is(.reminderCheckText, .debtToggleInline .toggleLabelText),
        body[data-tema="dark"] .sharedProPage :is(.reminderCheckText, .debtToggleInline .toggleLabelText) {
          color: #f8fafc !important;
          opacity: 1;
        }
        body.dark .sharedProPage .financeAmount.neutral,
        [data-theme="dark"] .sharedProPage .financeAmount.neutral,
        body[data-tema="dark"] .sharedProPage .financeAmount.neutral {
          color: rgba(241, 245, 249, .88) !important;
        }

        @media (max-width: 430px) {
          .sharedProPage .financeCompactFilterCard.sharedPeriodFilterCard {
            display: grid;
            grid-template-columns: 1fr;
            gap: 7px;
            padding: 10px;
          }
          .sharedProPage .sharedPeriodFilterCard .financeCompactFilterBody {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            width: 100%;
          }
          .sharedProPage .sharedPeriodFilterCard .financePeriodPills {
            grid-column: 1 / -1;
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
          .sharedProPage .sharedPeriodFilterCard .financePeriodPills .periodBtn { width: 100%; }
          .sharedProPage .sharedExpensePremiumGrid { grid-template-columns: minmax(0, 1fr); }
          .sharedProPage .sharedPremiumField { min-height: 52px; }
          .sharedProPage .sharedHeroMetrics {
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 6px;
          }
          .sharedProPage .sharedMetricPill { padding: 9px 7px; }
          .sharedProPage .tableRow { gap: 8px; padding-inline: 10px; }
          .sharedProPage .financeAmount {
            max-width: 92px;
            overflow-wrap: anywhere;
            text-align: right;
          }
          .sharedProPage .rowDeleteBtn { min-width: 42px; padding-inline: 8px; }
        }

      `}</style>
      <div className="sharedHeroCard">
        <div className="sharedHeroTop">
          <div className="sharedHeroText">
            <span className="financeEyebrow">{totalExpenseLabel}</span>
            <h2>{paraFormatla(toplamFiltreliHarcama)}</h2>
            <p>{t("expense.monthlyTotal")} · {donemKayitMetni} {filtreliHarcama.length} {t("expense.monthRecords")}</p>
          </div>
          <div className="sharedHeroBadge" aria-hidden="true">💳</div>
        </div>
        <div className="sharedHeroMetrics">
          <div className="sharedMetricPill">
            <small>{t("expense.perPerson")}</small>
            <strong>{paraFormatla(kisiBasi)}</strong>
            <span>{t("expense.perPersonSub")}</span>
          </div>
          <div className="sharedMetricPill">
            <small>{t("expense.topSpender")}</small>
            <strong>{enCokOdeyen?.isim || t("expense.noTopSpender")}</strong>
            <span>{enCokOdeyen ? paraFormatla(enCokOdeyen.tutar) : t("expense.noTopSpenderData")}</span>
          </div>
          <div className="sharedMetricPill">
            <small>{t("expense.heaviest")}</small>
            <strong>{enYogunKategori?.kategori ? harcamaKategoriEtiketi(enYogunKategori.kategori) : t("expense.noTopCategory")}</strong>
            <span>{enYogunKategori ? paraFormatla(enYogunKategori.tutar) : t("expense.noCategory")}</span>
          </div>
        </div>
      </div>

      <div className="darkCard financeCompactFilterCard sharedPeriodFilterCard">
        <div className="financeCompactFilterHead">
          <span className="cardHeaderTitle">{lang === "en" ? "Period Filter" : "Dönem Filtresi"}</span>
        </div>
        <div className="financeCompactFilterBody">
          <div className="financePeriodPills noSwipeBack" data-no-swipe-back="true">
            {["Aylık", "Yıllık"].map((x) => (
              <button
                key={x}
                type="button"
                className={harcamaPeriyot === x ? "periodBtn active" : "periodBtn"}
                onClick={() => setHarcamaPeriyot(x)}
              >
                {lang === "en" ? (x === "Aylık" ? "Monthly" : "Yearly") : x}
              </button>
            ))}
          </div>
          {harcamaPeriyot === "Aylık" && (
            <div className="inputLabelGroup compactFilterInputGroup">
              <label className="darkLabel">{lang === "en" ? "Month" : "Ay"}</label>
              <select className="darkInput" value={harcamaSeciliAy} onChange={(e) => setHarcamaSeciliAy(Number(e.target.value))}>
                <option value={-1}>{lang === "en" ? "All Months" : "Tüm Aylar"}</option>
                {AYLAR.map((a, i) => <option key={i} value={i}>{a}</option>)}
              </select>
            </div>
          )}
          <div className="inputLabelGroup compactFilterInputGroup">
            <label className="darkLabel">{lang === "en" ? "Year" : "Yıl"}</label>
            <select className="darkInput" value={harcamaSeciliYil} onChange={(e) => setHarcamaSeciliYil(Number(e.target.value))}>
              {yilSecenekleri.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="darkCard sharedCategorySummaryCard">
        <div className="cardHeader"><span className="cardHeaderTitle">{topCategoryTitle}</span></div>
        <MiniBarList items={kategoriOzetleri} total={toplamFiltreliHarcama} paraFormatla={paraFormatla} emptyLabel={t("expense.noCategory")} categoryLabel={harcamaKategoriEtiketi} limit={3} />
      </div>

      <div className="tableCard collapsibleTableCard" style={{marginBottom: "12px"}}>
        <div className="tableCardHeader collapsibleHeader" onClick={() => setKisiBazliAcik(!kisiBazliAcik)}>
          <span>{t("expense.byPersonTitle")}</span>
          <button className="miniToggle" type="button" onClick={(e) => { e.stopPropagation(); setKisiBazliAcik(!kisiBazliAcik); }}>
            {kisiBazliAcik ? "−" : "+"}
          </button>
        </div>
        {kisiBazliAcik && (
          <>
            {kisiBazliGorunen.length === 0 ? (
              <div className="emptyState compact">{t("expense.byPersonEmpty")}</div>
            ) : kisiBazliGorunen.map((k, idx) => (
              <div key={k.isim} className={`tableRow ${idx === kisiBazliGorunen.length - 1 && !borcHesabi ? "tableRowLast" : ""}`}>
                <div className="tableRowContent">
                  <ExpandableText text={k.isim} className="tableRowTitle" maxLength={34} />
                  <span className="tableRowSub">{t("expense.monthlyPaid")}</span>
                </div>
                <span className={`financeAmount ${Number(k.tutar || 0) === 0 ? "neutral" : "red"}`}>{paraFormatla(k.tutar)}</span>
              </div>
            ))}
            <label className="toggleLine debtToggleInline">
              <span className="toggleLabelText">{t("expense.showDebt")}</span>
              <div className={`toggleSwitch ${borcHesabi ? "on" : ""}`} onClick={borcHesabiDegistir}>
                <div className="toggleThumb" />
              </div>
            </label>
            {borcHesabi && borcOzetiFiltreli.length > 0 && (
              <>
                <div className="tableCardSubHeader">{t("expense.debtTitle")}</div>
                {borcOzetiFiltreli.map((k, idx) => (
                  <div key={k.isim} className={`tableRow ${idx === borcOzetiFiltreli.length - 1 ? "tableRowLast" : ""}`}>
                    <div className="tableRowContent">
                      <ExpandableText text={k.isim} className="tableRowTitle" maxLength={34} />
                      <span className="tableRowSub">{k.fark >= 0 ? t("expense.creditor") : t("expense.debtor")}</span>
                    </div>
                    <span className={`financeAmount ${k.fark >= 0 ? "green" : "red"}`}>{k.fark >= 0 ? "+" : ""}{paraFormatla(Math.abs(k.fark))}</span>
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </div>


      {canAdd && (
        <div className={`sharedExpenseAddCard ${harcamaFormAcik ? "open" : "closed"}`}>
          <button
            className="sharedExpenseAddHeader"
            type="button"
            onClick={() => setHarcamaFormAcik(!harcamaFormAcik)}
            aria-expanded={harcamaFormAcik}
          >
            <span className="sharedExpenseAddIcon" aria-hidden="true">+</span>
            <span className="sharedExpenseAddCopy">
              <strong>{t("expense.add")}</strong>
            </span>
            <span className="sharedExpenseAddArrow" aria-hidden="true">{harcamaFormAcik ? "⌃" : "⌄"}</span>
          </button>


          {harcamaFormAcik && (
            <div className="sharedExpenseAddBody">
              {/* ── Fiş Tara ── */}
              {!fisScanYukleni && !fistScanAcik && (
                <div className="scanSourcePicker scanInlinePanel compactInsideForm">
                  <button className="scanSourceBtn" type="button" onClick={() => handleFisScan("kamera")} disabled={fisScanYukleni}>
                    <span className="srcIcon">🧾</span>
                    {lang === "en" ? "Scan Receipt" : "Fiş Tara"}
                  </button>
                  <button className="scanSourceBtn" type="button" onClick={() => handleFisScan("galeri")} disabled={fisScanYukleni}>
                    <span className="srcIcon">🖼️</span>
                    {lang === "en" ? "From Gallery" : "Galeriden"}
                  </button>
                </div>
              )}
              {fisScanYukleni && (
                <div className="scanLoadingBanner">
                  <div className="loadingDot" />
                  {fisIlerleme > 0
                    ? `${lang === "en" ? "Reading" : "Okunuyor"} %${fisIlerleme}...`
                    : (lang === "en" ? "Scanning receipt..." : "Fiş taranıyor...")}
                </div>
              )}
              {fisScanHata && <div className="scanErrorMsg">{fisScanHata}</div>}
              {fisEklendi && (
                <div className="scanLoadingBanner" style={{ background: "rgba(16,185,129,0.1)", borderColor: "rgba(16,185,129,0.25)", color: "var(--accent-green)" }}>
                  ✓ {lang === "en" ? "Expenses added successfully" : "Harcamalar başarıyla eklendi"}
                </div>
              )}
              <div className="sharedExpensePremiumGrid">
                <label className={expenseValidation.fieldClass("title", "sharedPremiumField")} data-required-field="shared-expense-create-title">
                  <span>{t("expense.titlePlaceholder")}</span>
                  <input aria-invalid={expenseValidation.hasError("title")} value={harcamaBaslik} onChange={(e) => { setHarcamaBaslik(e.target.value); expenseValidation.clearError("title"); }} />
                  <b aria-hidden="true">Aa</b>
                </label>

                <label className={expenseValidation.fieldClass("amount", "sharedPremiumField")} data-required-field="shared-expense-create-amount">
                  <span>{t("expense.amountPlaceholder")}</span>
                  <input {...moneyInputProps} aria-invalid={expenseValidation.hasError("amount")} value={harcamaTutar} onChange={(e) => { setHarcamaTutar(formatMoneyInput(e.target.value)); expenseValidation.clearError("amount"); }} />
                  <b aria-hidden="true">₺</b>
                </label>

                <label className="sharedPremiumField">
                  <span>{t("common.category")}</span>
                  <select value={harcamaKategori} onChange={(e) => setHarcamaKategori(e.target.value)}>
                    {[...new Set([...(harcamaKategorileri || []), "Kredi Kartı"])].map((k) => <option key={k} value={k}>{harcamaKategoriEtiketi(k)}</option>)}
                  </select>
                  <b aria-hidden="true">⌄</b>
                </label>

                <label className="sharedPremiumField">
                  <span>{t("common.date")}</span>
                  <input type="date" value={harcamaTarih} onChange={(e) => setHarcamaTarih(e.target.value)} />
                  <b aria-hidden="true">20</b>
                </label>
              </div>

              <ReminderWarning message={reminderAktif ? reminderError : ""} />
              <FormValidationNotice
                errors={expenseValidation.errors}
                labels={{ title: t("expense.titlePlaceholder"), amount: t("expense.amountPlaceholder") }}
              />
              <AsyncActionButton className="sharedExpenseSaveButton" type="button" onAction={handleHarcamaEkle} busyText={t("common.saving")}>
                ✓ {t("expense.add")}
              </AsyncActionButton>
              <ReminderPanel
                aktif={reminderAktif}
                setAktif={setReminderAktif}
                tarih={reminderTarih}
                setTarih={setReminderTarih}
                saat={reminderSaat}
                setSaat={setReminderSaat}
                tekrar={reminderTekrar}
                setTekrar={setReminderTekrar}
                mod="datetime"
                etiket={t("expense.reminder")}
              />
            </div>
          )}
        </div>
      )}

      <div className="tableCard">
        <div
          className="harcamaListeHeader"
          onClick={() => setHarcamaListeGenisletildi(v => !v)}
        >
          <span>{t("expense.listTitle")}</span>
          <button
            type="button"
            className={`harcamaListeToggleBtn${harcamaListeGenisletildi ? " acik" : ""}`}
            onClick={(e) => { e.stopPropagation(); setHarcamaListeGenisletildi(v => !v); }}
            aria-label={harcamaListeGenisletildi ? t("common.collapse") : t("common.expand")}
          >
            ▾
          </button>
        </div>
        {siraliHarcama.length === 0 ? (
          <div className="emptyState">{t("expense.empty")}</div>
        ) : (
          <div className={`sharedExpenseScrollList${harcamaListeGenisletildi ? " genisletildi" : ""}`}>
            {gorunenHarcama.map((h, idx) => (
              <div key={h.id} className={`tableRow ${idx === gorunenHarcama.length - 1 ? "tableRowLast" : ""}`}>
                <div className="financeIndicator expenseBar" />
                <div className="tableRowContent">
                  <ExpandableText text={h.baslik} className="tableRowTitle" maxLength={34} />
                  <span className="tableRowSub">{harcamaKategoriEtiketi(h.kategori)} · {tarihOkunakli(h.tarih)} · {h.ekleyen}</span>
                </div>
                <span className="financeAmount red">-{paraFormatla(h.tutar)}</span>
                {(canDeleteAll || (canDeleteOwn && h.ekleyenUid === user?.uid)) && (
                  <button className="rowDeleteBtn" onClick={() => harcamaSil(h)}>{t("common.delete")}</button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {fistScanAcik && (
        <ReceiptPreviewModal
          items={fisKalemleri}
          categories={harcamaKategorileri}
          categoryLabel={harcamaKategoriEtiketi}
          onConfirm={handleFisConfirm}
          onClose={() => setFisScanAcik(false)}
        />
      )}

    </div>
  );
}

export default SharedExpensesPage;
